// #ASK_DOPE_BTN_V1 (2026-10-22) — Aaron: "open DOPE, ask a question, and it produce the answer."
// Header mic button -> small panel. Talk (browser speech-to-text) or type; the 'ask-dope' Edge Function answers
// with READ-ONLY lookups; the answer is shown and spoken in the device's best voice (picker remembers the choice).
// Owners only for now: Bill, Aaron, Ashlie (the Edge Function enforces the same list server-side).
// #ASK_DOPE_CHAT_V2 (2026-10-22) — Bill: "to ask another question I had to close and open again, doesn't hold a conversation."
// Panel is now a running chat (Q/A stay on screen, follow-ups keep context); mic restarts reliably on iPhone (voice is stopped and
// the mic waits a beat before listening; a refusal shows a message instead of failing silently); input clears after each ask.
import { useState, useRef, useEffect } from "react";
import { SUPA_URL, _supaHdr, _currentUpn } from "./appShared";

export const ASK_DOPE_USERS = ["bill.payne@dakotautility.com", "aaron.graff@dakotautility.com", "ashlie.graff@dakotautility.com"];
const VOICE_KEY = "dope_ask_voice";
// #ASK_DOPE_VOICES_V3 (2026-10-22) — iPhone doesn't hand the downloaded Enhanced voices (Ava, Evan) to websites — only to Apple's
// own apps — so Safari offered just Samantha + Tessa, and Tessa was too robotic. Tessa dropped; Samantha is the iPhone default.
// Ava / Evan / Kate / Stephanie stay listed for any device (Mac Safari) that does expose them.
const GOOD = [/^Ava\b/i, /^Evan\b/i, /^Kate\b/i, /^Stephanie\b/i, /^Samantha\b/i,
  /^Google US English$/i, /^Google UK English Female$/i, /Microsoft (Ava|Andrew|Emma|Jenny).*Natural/i];
const PREFERRED = [/^Ava \((Enhanced|Premium)\)/i, /Ava.*Natural/i, /^Google US English$/i, /^Samantha/i, /^Evan/i, /^Kate/i, /^Stephanie/i, /^Ava\b/i];

function listVoices() {
  let all = [];
  try { all = (window.speechSynthesis?.getVoices() || []).filter(v => /^en/i.test(v.lang)); } catch { return []; }
  const good = all.filter(v => GOOD.some(re => re.test(v.name)));
  const seen = new Map();
  for (const v of good) { const base = v.name.replace(/\s*\((Enhanced|Premium|Compact)\)/i, ""); const prev = seen.get(base);
    if (!prev || /Premium|Enhanced/i.test(v.name)) seen.set(base, v); }
  const out = [...seen.values()];
  return out.length ? out : all;
}
function pickVoice(voices) {
  let saved = ""; try { saved = localStorage.getItem(VOICE_KEY) || ""; } catch {}
  if (saved) { const v = voices.find(x => x.name === saved); if (v) return v; }
  for (const re of PREFERRED) { const v = voices.find(x => re.test(x.name)); if (v) return v; }
  return voices[0] || null;
}

export default function AskDopeButton() {
  const upn = String(_currentUpn() || "").toLowerCase();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [thread, setThread] = useState([]);   // [{q, a, err}]
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [micMsg, setMicMsg] = useState("");
  const [voices, setVoices] = useState([]);
  const [voiceName, setVoiceName] = useState("");
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem("dope_ask_muted") === "1"; } catch { return false; } });
  const recRef = useRef(null);
  const inputRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    const load = () => { const v = listVoices(); setVoices(v); const p = pickVoice(v); if (p) setVoiceName(p.name); };
    load();
    try { window.speechSynthesis.onvoiceschanged = load; } catch {}
  }, []);
  useEffect(() => { try { endRef.current?.scrollIntoView({ block: "end" }); } catch {} }, [thread, busy]);

  if (!ASK_DOPE_USERS.includes(upn)) return null;

  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const speak = (text) => {
    if (muted || !text) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = voices.find(x => x.name === voiceName); if (v) u.voice = v;
      u.rate = 1.02;
      window.speechSynthesis.speak(u);
    } catch {}
  };
  const stopTalk = () => { try { window.speechSynthesis.cancel(); } catch {} };

  const ask = async (text) => {
    const question = String(text ?? q).trim();
    if (!question || busy) return;
    setBusy(true); setMicMsg(""); stopTalk(); setQ("");
    const history = thread.filter(t => t.a).slice(-3).flatMap(t => [{ role: "user", content: t.q }, { role: "assistant", content: t.a }]);
    setThread(th => [...th, { q: question }]);
    try {
      const res = await fetch(`${SUPA_URL}/functions/v1/ask-dope`, {
        method: "POST", headers: await _supaHdr(),
        body: JSON.stringify({ question, history }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.error) throw new Error(j.error || `HTTP ${res.status}`);
      const a = j.answer || "(no answer)";
      setThread(th => th.map((t, i) => i === th.length - 1 ? { ...t, a } : t));
      speak(a);
    } catch (e) {
      const err = String(e.message || e);
      setThread(th => th.map((t, i) => i === th.length - 1 ? { ...t, err } : t));
    }
    setBusy(false);
  };

  const listen = () => {
    if (!SR) { inputRef.current?.focus(); return; }
    if (listening) { try { recRef.current?.stop(); } catch {} return; }
    stopTalk(); setMicMsg("");
    try { recRef.current?.abort(); } catch {}
    // iPhone won't hand the mic over while it's still talking — give the speaker a beat to let go
    setTimeout(() => {
      const r = new SR(); recRef.current = r;
      r.lang = "en-US"; r.interimResults = true; r.continuous = false;
      let finalText = "";
      r.onresult = (e) => { let t = ""; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript; finalText = t; setQ(t); };
      r.onerror = (e) => { setListening(false);
        if (e.error === "not-allowed" || e.error === "service-not-allowed") setMicMsg("Mic blocked — allow the microphone for this site in Settings.");
        else if (e.error !== "no-speech" && e.error !== "aborted") setMicMsg("Mic didn't start — tap 🎙 again."); };
      r.onend = () => { setListening(false); if (finalText.trim()) ask(finalText); };
      try { r.start(); setListening(true); } catch { setListening(false); setMicMsg("Mic didn't start — tap 🎙 again."); }
    }, 250);
  };

  const openPanel = () => {
    try { const u = new SpeechSynthesisUtterance(" "); u.volume = 0; window.speechSynthesis.speak(u); } catch {}   // iPhone: allow talking after this tap
    setOpen(true);
  };

  const gold = "#C9A227";
  const last = thread[thread.length - 1];
  return (
    <>
      <button type="button" onClick={() => (open ? (stopTalk(), setOpen(false)) : openPanel())} title="Ask DOPE"
        style={{ alignSelf: "center", display: "flex", alignItems: "center", gap: 5, background: open ? gold : "transparent",
          color: open ? "#111" : gold, border: `1.5px solid ${gold}`, borderRadius: 20, padding: "5px 10px",
          fontSize: 12, fontWeight: 900, cursor: "pointer", whiteSpace: "nowrap", marginRight: 6 }}>
        <span style={{ fontSize: 14, lineHeight: 1 }}>🎙</span> Ask
      </button>
      {open && (
        <div style={{ position: "fixed", top: 70, right: 10, left: 10, maxWidth: 520, margin: "0 0 0 auto", zIndex: 1000,
          background: "#16181C", border: `1.5px solid ${gold}`, borderRadius: 12, padding: 12, color: "#EDEEF0",
          boxShadow: "0 12px 40px rgba(0,0,0,.6)", fontFamily: "inherit", display: "flex", flexDirection: "column", maxHeight: "calc(100dvh - 90px)" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 900, letterSpacing: 1.5, color: gold, fontSize: 12, flex: 1 }}>ASK DOPE</div>
            {thread.length > 0 && <button type="button" onClick={() => { stopTalk(); setThread([]); setQ(""); }} style={iconBtn} title="Start over">New</button>}
            <button type="button" onClick={() => { setMuted(m => { const n = !m; try { localStorage.setItem("dope_ask_muted", n ? "1" : "0"); } catch {} if (n) stopTalk(); return n; }); }}
              title={muted ? "Voice off" : "Voice on"} style={iconBtn}>{muted ? "🔇" : "🔊"}</button>
            <button type="button" onClick={() => { stopTalk(); setOpen(false); }} style={iconBtn} title="Close">✕</button>
          </div>

          {thread.length > 0 && (
            <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", marginBottom: 10, display: "flex", flexDirection: "column", gap: 8, maxHeight: "50vh" }}>
              {thread.map((t, i) => (
                <div key={i}>
                  <div style={{ alignSelf: "flex-end", marginLeft: "auto", maxWidth: "85%", width: "fit-content", background: `${gold}22`, border: `1px solid ${gold}55`,
                    borderRadius: 10, padding: "6px 10px", fontSize: 14, color: "#F5E7B8" }}>{t.q}</div>
                  <div style={{ marginTop: 6, maxWidth: "95%", background: "#0E0F12", border: "1px solid #2A2D32", borderRadius: 10, padding: "8px 11px",
                    fontSize: 15, lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                    {t.err ? <span style={{ color: "#FCA5A5" }}>{t.err}</span> : t.a ? t.a : <span style={{ color: "#9AA0A8" }}>Looking it up…</span>}
                    {t.a && i === thread.length - 1 && !busy && <button type="button" onClick={() => speak(t.a)} style={{ ...iconBtn, fontSize: 11, padding: "1px 7px", marginLeft: 8 }}>↻</button>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={listen} disabled={busy}
              title={SR ? (listening ? "Stop" : "Talk") : "Tap the box and use the keyboard mic"}
              style={{ width: 48, height: 44, flexShrink: 0, borderRadius: 10, border: "none", cursor: "pointer", fontSize: 20,
                background: listening ? "#EF4444" : gold, color: "#111", opacity: busy ? 0.5 : 1 }}>{listening ? "■" : "🎙"}</button>
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              placeholder={listening ? "Listening…" : last ? "Ask a follow-up…" : "Ask anything — e.g. What job is Felix on?"}
              style={{ flex: 1, minWidth: 0, background: "#0E0F12", color: "#EDEEF0", border: "1px solid #3A3D42", borderRadius: 10,
                padding: "0 12px", fontSize: 16 }} />
            <button type="button" onClick={() => ask()} disabled={busy || !q.trim()}
              style={{ flexShrink: 0, borderRadius: 10, border: `1px solid ${gold}`, background: "transparent", color: gold,
                fontWeight: 900, padding: "0 12px", cursor: "pointer", opacity: busy || !q.trim() ? 0.5 : 1 }}>Ask</button>
          </div>
          {micMsg && <div style={{ marginTop: 6, fontSize: 12, color: "#FCD34D" }}>{micMsg}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 11, color: "#9AA0A8" }}>
            <span>{busy ? "Thinking…" : ""}</span>
            <span style={{ flex: 1 }} />
            {voices.length > 0 && (
              <select value={voiceName} onChange={(e) => { setVoiceName(e.target.value); try { localStorage.setItem(VOICE_KEY, e.target.value); } catch {} }}
                style={{ maxWidth: 170, background: "#0E0F12", color: "#9AA0A8", border: "1px solid #2A2D32", borderRadius: 6, fontSize: 11, padding: "2px 4px" }}>
                {voices.map(v => <option key={v.name} value={v.name}>{v.name}</option>)}
              </select>
            )}
          </div>
        </div>
      )}
    </>
  );
}
const iconBtn = { background: "transparent", border: "1px solid #3A3D42", color: "#EDEEF0", borderRadius: 6, padding: "2px 8px",
  marginLeft: 6, cursor: "pointer", fontSize: 13 };
